// All sub-page components (About, Contact, Privacy, Terms, Security,
// Early Access, Sample Report, Integrations, Use Cases, Insights/Blog).
// Each page composes <SiteNav /> + content sections + <PageCta /> + <Footer />.

import React from 'react';
import { USE_CASE_SLUGS } from './use-case-routes.js';
import { relatedUseCases, useCaseLabels } from './use-case-content.js';
import { PageHero, PageCta } from './Shell.jsx';
import { getActiveLocale, localizedHref } from './i18n/bootstrap.js';
import { t } from './i18n/useT.js';
import { ModalRoot } from './Forms.jsx';
import { TurnstileWidget, isTurnstileEnabled } from './Turnstile.jsx';
import {
  PILOT_PRICE_FIT_OPTIONS,
  SUBSCRIPTION_FIT_OPTIONS,
  PILOT_PRICE,
  CURRENT_PLANS,
  TRIAL_PLAN_IDS,
  FEATURED_PLAN,
  CREDIT_PACKS,
  AI_ANALYSIS,
  PLAN_CTA,
  PILOT_CTA_HREF,
  CUSTOM_CTA_HREF,
  fillPricing,
  formatCap,
  formatCount,
  formatUsd,
} from './pricing.js';
import { appUrl } from './app-origin.js';
import { BLOG_POSTS, AUTHORS, getBlogPost, getRelatedPosts, formatBlogDate } from './blogPosts.js';
import {
  ConnectedToolsSection,
  ConfidenceSection,
} from './DashboardWidgets.jsx';
import {
  ClientQuestionHeroVisual,
  EarlyAccessStackHeroVisual,
  EvidenceGraphHeroVisual,
  IntegrationsToolsHeroVisual,
  SampleReportHeroVisual,
  SavedReportsHeroVisual,
  UseCasesEvidenceHeroVisual,
} from './PageHeroVisuals.jsx';
import { useRegion } from './Region.jsx';
import {
  IMPORT_CAPABILITY_IDS,
  LIMITED_SETUP_NAMES,
  ROADMAP_GROUPS,
  REGIONAL_LOGO_CLUSTERS,
  getCatalogIntegration,
} from './integration-catalog.js';
import { IntegrationLogo } from './Integrations.jsx';

// =============================================================
// Tiny shared helpers
// =============================================================
import { PageShell } from './PageShell.jsx';

// Renders the canonical sample-report CTA. The global helper navigates to the
// /sample-report/start flow (with ?source=) — see scripts/Forms.jsx.
function SampleReportButton({ source, label = t('cta.downloadSample'), size = 'lg', variant = 'primary' }) {
  const cls = `sw-btn sw-btn-${variant} ${size === 'lg' ? 'sw-btn-lg' : ''}`;
  return (
    <button
      className={cls}
      onClick={() => window.openSampleReport && window.openSampleReport(source)}
    >
      <span className="sw-btn-label">{label}</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
    </button>
  );
}

function Field({ label, required, error, hint, htmlFor, children }) {
  return (
    <div className="sw-field">
      <label className="sw-field-label" htmlFor={htmlFor}>
        <span>
          {label}
          {required ? <span className="sw-required-asterisk" aria-hidden="true">*</span> : null}
        </span>
        {error ? <span className="sw-field-error">{error}</span> : null}
      </label>
      {children}
      {hint ? <span className="sw-field-hint">{hint}</span> : null}
    </div>
  );
}

function FieldGroup({ label, required, error, hint, children }) {
  return (
    <div className="sw-field">
      <div className="sw-field-label">
        <span>
          {label}
          {required ? <span className="sw-required-asterisk" aria-hidden="true">*</span> : null}
        </span>
        {error ? <span className="sw-field-error">{error}</span> : null}
      </div>
      {children}
      {hint ? <span className="sw-field-hint">{hint}</span> : null}
    </div>
  );
}

// =============================================================
// 1. About
// =============================================================

// Compact contextual icons rendered alongside About section headings.
function SectionIcon({ name }) {
  const stroke = '#2563EB';
  const accent = '#14B8A6';
  switch (name) {
    case 'compass':
      return (
        <svg className="sw-about-icon" viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="32" r="22" fill="none" stroke={stroke} strokeWidth="1.6" opacity="0.45" />
          <circle cx="32" cy="32" r="14" fill="none" stroke={stroke} strokeWidth="1.6" opacity="0.6" />
          <path
            className="sw-about-icon-needle"
            d="M32 18 L36 32 L32 46 L28 32 Z"
            fill={accent}
            stroke={stroke}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <circle cx="32" cy="32" r="2.4" fill={stroke} />
        </svg>
      );
    case 'converge':
      return (
        <svg className="sw-about-icon" viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="32" r="6" fill={accent} opacity="0.95" />
          <path
            className="sw-about-icon-converge"
            d="M32 32 L10 12 M32 32 L54 12 M32 32 L8 38 M32 32 L56 38 M32 32 L20 56 M32 32 L44 56"
            stroke={stroke}
            strokeWidth="1.6"
            strokeLinecap="round"
            opacity="0.7"
          />
          <circle cx="10" cy="12" r="2.2" fill={stroke} />
          <circle cx="54" cy="12" r="2.2" fill={stroke} />
          <circle cx="8" cy="38" r="2.2" fill={stroke} />
          <circle cx="56" cy="38" r="2.2" fill={stroke} />
          <circle cx="20" cy="56" r="2.2" fill={stroke} />
          <circle cx="44" cy="56" r="2.2" fill={stroke} />
          <path
            d="M27 32 l4 4 l7 -7"
            stroke="#F8FAFC"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'team':
      return (
        <svg className="sw-about-icon" viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="20" cy="22" r="6" fill={stroke} opacity="0.85" />
          <path d="M8 46 q12 -14 24 0 z" fill={stroke} opacity="0.85" />
          <circle cx="44" cy="22" r="6" fill={accent} />
          <path d="M32 46 q12 -14 24 0 z" fill={accent} />
          <circle cx="32" cy="40" r="7" fill="#0F172A" />
          <path d="M18 60 q14 -16 28 0 z" fill="#0F172A" />
          <line x1="20" y1="22" x2="44" y2="22" stroke="#CBD5E1" strokeWidth="1.4" strokeDasharray="2 3" />
        </svg>
      );
    case 'shield':
      return (
        <svg className="sw-about-icon" viewBox="0 0 64 64" aria-hidden="true">
          <path
            d="M32 8 L52 16 V32 C52 44 42 54 32 58 C22 54 12 44 12 32 V16 Z"
            fill="none"
            stroke={stroke}
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M32 12 L48 18 V32 C48 42 40 50 32 53 C24 50 16 42 16 32 V18 Z"
            fill={stroke}
            opacity="0.08"
          />
          <path
            className="sw-about-icon-check"
            d="M22 32 l7 7 l13 -14"
            stroke={accent}
            strokeWidth="2.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

export function AboutPage() {
  const principles = t('about.principles');

  return (
    <PageShell
      currentPath="/about"
      title={t('meta.about.title')}
      description={t('meta.about.description')}
    >
      <div className="sw-page-about">
      <PageHero
        showcase
        overlapNav
        eyebrow={t('about.hero.eyebrow')}
        title={<>{t('about.hero.titlePre')}<span className="sw-hero-title-accent">{t('about.hero.titleAccent')}</span>{t('about.hero.titlePost')}</>}
        lead={t('about.hero.lead')}
        visual={<EvidenceGraphHeroVisual />}
      />

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-section-narrow">
            <div className="sw-about-head">
              <div className="sw-about-head-text">
                <div className="sw-eyebrow sw-eyebrow-blue">{t('about.why.eyebrow')}</div>
                <h2 className="sw-h2">{t('about.why.title')}</h2>
              </div>
              <div className="sw-about-head-icon"><SectionIcon name="compass" /></div>
            </div>
            {t('about.why.p').map((para, i) => (
              <p className="sw-lead" style={{ marginTop: 16 }} key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-soft">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-about-head">
              <div className="sw-about-head-text">
                <div className="sw-eyebrow">{t('about.problem.eyebrow')}</div>
                <h2 className="sw-h2">{t('about.problem.title')}</h2>
                <p className="sw-lead" style={{ marginTop: 12 }}>
                  {t('about.problem.lead')}
                </p>
              </div>
              <div className="sw-about-head-icon"><SectionIcon name="converge" /></div>
            </div>
          </div>
          <div className="sw-card-grid-3">
            {t('about.problem.cards').map((c, i) => (
              <div className="sw-card" key={i}>
                <h3 className="sw-card-title">{c.title}</h3>
                <p className="sw-card-body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-section-narrow">
            <div className="sw-about-head">
              <div className="sw-about-head-text">
                <div className="sw-eyebrow sw-eyebrow-blue">{t('about.audience.eyebrow')}</div>
                <h2 className="sw-h2">{t('about.audience.title')}</h2>
              </div>
              <div className="sw-about-head-icon"><SectionIcon name="team" /></div>
            </div>
            {t('about.audience.p').map((para, i) => (
              <p className="sw-lead" style={{ marginTop: 16 }} key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-soft">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-about-head">
              <div className="sw-about-head-text">
                <div className="sw-eyebrow sw-eyebrow-teal">{t('about.principlesHead.eyebrow')}</div>
                <h2 className="sw-h2">{t('about.principlesHead.title')}</h2>
              </div>
              <div className="sw-about-head-icon"><SectionIcon name="shield" /></div>
            </div>
          </div>
          <div className="sw-numbered">
            {principles.map((p, i) => (
              <div key={i} className="sw-numbered-item sw-reveal">
                <div className="sw-numbered-num">{String(i + 1).padStart(2, '0')}</div>
                <div>
                  <h3 className="sw-numbered-title">{p.t}</h3>
                  <p className="sw-numbered-body">{p.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCta
        source="about"
        title={<>{t('about.cta.titleL1')}<br/>{t('about.cta.titleL2')}</>}
        lead={t('about.cta.lead')}
        secondary={{ href: '/pricing', label: t('about.cta.secondary') }}
      />
      </div>
    </PageShell>
  );
}

// =============================================================
// 2. Contact
// =============================================================
// Submitted values, in the order of the localized contact.reasons labels.
// functions/api/contact.js accepts exactly these (plus retired values).
const CONTACT_REASONS = [
  'Request a demo',
  'Product and plans',
  'Integration setup',
  'Assisted delivery review',
  'Security and data handling',
  'General question',
];

// Roles tailored to our segment: software agencies, consultancies, and
// engineering orgs that bill clients for delivery.
const CONTACT_ROLES = [
  'Founder / Owner',
  'CEO / Managing Director',
  'COO / Head of Operations',
  'CTO / VP of Engineering',
  'Engineering Manager / Team Lead',
  'Head of Delivery / Delivery Manager',
  'Project / Program Manager',
  'Product Manager',
  'Account / Client Services Lead',
  'Finance / Commercial',
  'Other',
];

export function ContactPage() {
  const [form, setForm] = React.useState({
    fullName: '', workEmail: '', companyName: '', role: '', reason: '', message: '',
    turnstileToken: '',
  });
  const [errors, setErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reason') === 'demo') {
      setForm(f => ({ ...f, reason: 'Request a demo' }));
    }
  }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = t('contact.err.fullName');
    if (!form.workEmail.trim()) errs.workEmail = t('contact.err.workEmail');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail)) errs.workEmail = t('contact.err.email');
    if (!form.companyName.trim()) errs.companyName = t('contact.err.companyName');
    if (!form.reason) errs.reason = t('contact.err.reason');
    if (!form.message.trim()) errs.message = t('contact.err.message');
    if (isTurnstileEnabled() && !form.turnstileToken) errs.turnstileToken = t('contact.err.verify');
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setSubmitError('');
    // api-client.js installs window.onContactSubmit (POST /api/marketing/contact);
    // it rejects on a failed request. Only a delivered message shows success.
    const send = (typeof window !== 'undefined' && typeof window.onContactSubmit === 'function')
      ? Promise.resolve(window.onContactSubmit({ ...form }))
      : Promise.reject(new Error('contact submission unavailable'));
    send.then(
      () => { setSubmitting(false); setSubmitted(true); },
      () => { setSubmitting(false); setSubmitError(t('contact.err.send')); },
    );
  };

  return (
    <PageShell
      currentPath="/contact"
      title={t('meta.contact.title')}
      description={t('meta.contact.description')}
    >
      <div className="sw-page-contact">
      <PageHero
        showcase
        overlapNav
        eyebrow={t('contact.hero.eyebrow')}
        title={t('contact.hero.title')}
        lead={t('contact.hero.lead')}
        visual={<ClientQuestionHeroVisual />}
      />

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-section-narrow">
            <div className="sw-page-form">
              {submitted ? (
                <ContactSuccess />
              ) : (
                <form className="sw-form sw-form--grouped" onSubmit={submit} noValidate>
                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('contact.groupAbout')}</legend>
                    <div className="sw-form-row">
                      <Field label={t('contact.fullName')} required error={errors.fullName} htmlFor="contact-name">
                        <input id="contact-name" className="sw-input" autoComplete="name"
                          aria-invalid={!!errors.fullName}
                          value={form.fullName} placeholder={t('contact.fullNamePh')}
                          onChange={e => set('fullName', e.target.value)}/>
                      </Field>
                      <Field label={t('contact.workEmail')} required error={errors.workEmail} htmlFor="contact-email">
                        <input id="contact-email" className="sw-input" type="email" autoComplete="email"
                          aria-invalid={!!errors.workEmail}
                          value={form.workEmail} placeholder={t('contact.workEmailPh')}
                          onChange={e => set('workEmail', e.target.value)}/>
                      </Field>
                    </div>
                    <div className="sw-form-row">
                      <Field label={t('contact.companyName')} required error={errors.companyName} htmlFor="contact-company">
                        <input id="contact-company" className="sw-input" autoComplete="organization"
                          aria-invalid={!!errors.companyName}
                          value={form.companyName} placeholder={t('contact.companyNamePh')}
                          onChange={e => set('companyName', e.target.value)}/>
                      </Field>
                      <Field label={t('contact.role')} htmlFor="contact-role">
                        <select id="contact-role" className="sw-input sw-select"
                          value={form.role} onChange={e => set('role', e.target.value)}>
                          <option value="">{t('contact.selectRole')}</option>
                          {CONTACT_ROLES.map((r, i) => <option key={r} value={r}>{t('contact.roles')[i]}</option>)}
                        </select>
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('contact.groupReason')}</legend>
                    <Field label={t('contact.reasonLabel')} required error={errors.reason} htmlFor="contact-reason">
                      <select id="contact-reason" className="sw-input sw-select"
                        aria-invalid={!!errors.reason}
                        value={form.reason} onChange={e => set('reason', e.target.value)}>
                        <option value="">{t('contact.selectOne')}</option>
                        {CONTACT_REASONS.map((r, i) => <option key={r} value={r}>{t('contact.reasons')[i]}</option>)}
                      </select>
                    </Field>
                    <Field label={t('contact.message')} required error={errors.message} htmlFor="contact-message"
                      hint={t('contact.messageHint')}>
                      <textarea id="contact-message" className="sw-input sw-textarea" rows={5}
                        aria-invalid={!!errors.message}
                        value={form.message}
                        placeholder={t('contact.messagePh')}
                        onChange={e => set('message', e.target.value)}/>
                    </Field>
                  </fieldset>

                  <fieldset className="sw-form-group sw-form-group--final">
                    {isTurnstileEnabled() ? (
                      <div className="sw-turnstile-wrap">
                        <TurnstileWidget
                          action="contact"
                          onToken={(token) => set('turnstileToken', token || '')}
                        />
                        {errors.turnstileToken ? (
                          <div className="sw-field-error sw-field-error--block">
                            {errors.turnstileToken}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {submitError ? (
                      <div className="sw-field-error sw-field-error--block" role="alert" style={{ marginBottom: 12 }}>
                        {submitError}
                      </div>
                    ) : null}
                    <div className="sw-form-foot sw-form-foot--end">
                      <button
                        type="button"
                        className="sw-textlink-quiet"
                        onClick={() => window.openSampleReport && window.openSampleReport('contact-page')}
                      >
                        {t('contact.sampleInstead')}
                      </button>
                      <button type="submit" className="sw-btn sw-btn-primary" disabled={submitting}>
                        <span className="sw-btn-label">{submitting ? t('contact.sending') : t('contact.send')}</span>
                      </button>
                    </div>
                  </fieldset>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      </div>
    </PageShell>
  );
}

function ContactSuccess() {
  return (
    <div className="sw-page-form-success">
      <div className="sw-success-mark" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>
      </div>
      <h3 className="sw-success-title">{t('contact.success.title')}</h3>
      <p className="sw-success-line">{t('contact.success.line1')}</p>
      <p className="sw-success-line sw-success-meta">
        {t('contact.success.line2')}
      </p>
      <a href={localizedHref('/')} className="sw-btn sw-btn-ghost"><span className="sw-btn-label">{t('contact.success.back')}</span></a>
    </div>
  );
}

// =============================================================
export { PrivacyPage, TermsPage, SecurityPage, SubprocessorsPage, DpaPage } from './LegalPages.jsx';

// =============================================================
// 5.5 Pricing
// =============================================================
// Every price, limit, trial and credit figure below comes from the pinned App
// catalog through pricing.js (SCO-508); the translated strings only carry
// {placeholders}. The concierge pilot and custom agreements are separate
// offers, labelled as such, and are not part of the plan catalog.
const CHECK_ICON = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8.4L6.5 11.4L12.5 4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ARROW_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);

// Only the authored emphasis marker is interpreted; all other copy stays text.
function PricingFeature({ text }) {
  return text.split(/(<strong>.*?<\/strong>)/g).map((part, index) => (
    part.startsWith('<strong>')
      ? <strong key={index}>{part.slice(8, -9)}</strong>
      : part
  ));
}

function planFeatures(plan, locale) {
  const f = (key, vars) => fillPricing(t(`pricing.features.${key}`), locale, vars);
  const items = [
    f(plan.activeProjects === 1 ? 'activeProjectsOne' : 'activeProjectsOther', { count: formatCount(plan.activeProjects, locale) }),
    t('pricing.features.sync')[plan.syncFrequency],
    f('repos'),
    f('history', { days: formatCount(plan.dataWindowDays, locale) }),
    plan.reportsPerMonth === 'unlimited' ? f('reportsUnlimited') : f('reports', { count: formatCount(plan.reportsPerMonth, locale) }),
    plan.includedCreditsPerPeriod > 0
      ? f('credits', { count: formatCount(plan.includedCreditsPerPeriod, locale) })
      : f('noCredits'),
  ];
  items.push(f('chat'));
  if (plan.qaAccess) items.push(f('qa'));
  if (plan.customRoles) items.push(f('customRoles'));
  return items;
}

function PlanCard({ plan, locale }) {
  const copy = t(`pricing.planCopy.${plan.plan}`);
  const featured = plan.plan === FEATURED_PLAN;
  const hasTrial = TRIAL_PLAN_IDS.includes(plan.plan);
  const free = plan.monthlyPriceUsdCents === 0;
  const cta = PLAN_CTA[plan.plan];
  return (
    <article className={'sw-pricing-card' + (featured ? ' is-featured' : '')} data-plan={plan.plan} data-trial={hasTrial ? 'card-required' : undefined} aria-labelledby={`sw-plan-${plan.plan}`}>
      <div className="sw-pricing-card-top">
        <h3 className="sw-pricing-card-name sw-pricing-card-name--plan" id={`sw-plan-${plan.plan}`}>{plan.label}</h3>
        <span className="sw-pricing-card-badge">{free ? t('pricing.freeBadge') : fillPricing(t('pricing.trialBadge'), locale)}</span>
      </div>
      <div className="sw-pricing-card-price">
        <span className="sw-pricing-card-amt" data-price-cents={plan.monthlyPriceUsdCents}>{formatUsd(plan.monthlyPriceUsdCents, locale)}</span>
        <span className="sw-pricing-card-cadence">{free ? t('pricing.freeCadence') : t('pricing.perMonth')}</span>
      </div>
      <p className="sw-pricing-card-summary">{copy.summary}</p>
      <ul className="sw-pricing-card-features">
        {planFeatures(plan, locale).map((feature) => (
          <li key={feature}>{CHECK_ICON}<span><PricingFeature text={feature} /></span></li>
        ))}
      </ul>
      <a
        href={appUrl('/sign-up')}
        data-cta={cta.source}
        aria-label={`${fillPricing(copy.cta, locale)} — ${plan.label}`}
        className={'sw-btn sw-pricing-card-cta ' + (featured ? 'sw-btn-primary' : 'sw-btn-outline')}
      >
        <span className="sw-btn-label">{fillPricing(copy.cta, locale)}</span>
        {ARROW_ICON}
      </a>
    </article>
  );
}

function OfferCard({ id, text, href, price }) {
  return (
    <article className="sw-pricing-card sw-pricing-card--offer" aria-labelledby={`sw-offer-${id}`}>
      <div className="sw-pricing-card-top">
        <span className="sw-pricing-card-eyebrow sw-pricing-card-eyebrow--muted">{text.eyebrow}</span>
      </div>
      <h3 className="sw-pricing-card-name" id={`sw-offer-${id}`}>{text.name}</h3>
      <div className="sw-pricing-card-price">
        <span className="sw-pricing-card-amt">{price}</span>
        <span className="sw-pricing-card-cadence">{text.cadence}</span>
      </div>
      <p className="sw-pricing-card-summary">{text.summary}</p>
      <ul className="sw-pricing-card-features">
        {text.features.map((feature) => <li key={feature}>{CHECK_ICON}<span><PricingFeature text={feature} /></span></li>)}
      </ul>
      <a href={localizedHref(href)} className="sw-btn sw-pricing-card-cta sw-btn-outline">
        <span className="sw-btn-label">{text.ctaLabel}</span>
        {ARROW_ICON}
      </a>
    </article>
  );
}

function PlanComparisonTable({ locale }) {
  const c = (key) => t(`pricing.compare.${key}`);
  const cap = (value) => formatCap(value, c('unlimited'), locale);
  const yesNo = (value) => (value ? c('yes') : c('no'));
  const rows = [
    ['price', (p) => formatUsd(p.monthlyPriceUsdCents, locale)],
    ['activeProjects', (p) => cap(p.activeProjects)],
    ['repos', () => c('multiple')],
    ['clients', (p) => cap(p.clients)],
    ['history', (p) => fillPricing(c('days'), locale, { count: cap(p.dataWindowDays) })],
    ['sync', (p) => c('sync')[p.syncFrequency]],
    ['reports', (p) => cap(p.reportsPerMonth)],
    ['qa', (p) => yesNo(p.qaAccess)],
    ['chat', () => c('yes')],
    ['recipients', (p) => cap(p.distributionRecipients)],
    ['bundles', (p) => cap(p.scheduledBundles)],
    ['customRoles', (p) => yesNo(p.customRoles)],
    ['credits', (p) => formatCount(p.includedCreditsPerPeriod, locale)],
  ];
  return (
    <div className="sw-pricing-table-wrap" role="region" aria-label={c('regionLabel')} tabIndex={0}>
      <table className="sw-pricing-table" data-pricing-table="plans">
        <caption>{c('caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{c('limitHeader')}</th>
            {CURRENT_PLANS.map((plan) => <th scope="col" key={plan.plan}>{plan.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, value]) => (
            <tr key={key} data-row={key}>
              <th scope="row">{c('rows')[key]}</th>
              {CURRENT_PLANS.map((plan) => <td key={plan.plan}>{value(plan)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditPriceTable({ locale }) {
  const k = (key) => t(`pricing.credits.${key}`);
  const credits = (count) => fillPricing(k(count === 1 ? 'creditValue' : 'creditsValue'), locale, { count: formatCount(count, locale) });
  const largest = AI_ANALYSIS.bands[AI_ANALYSIS.bands.length - 1];
  return (
    <div className="sw-pricing-table-wrap" role="region" aria-label={k('regionLabel')} tabIndex={0}>
      <table className="sw-pricing-table" data-pricing-table="ai-actions">
        <caption>{k('tableCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{k('colSize')}</th>
            <th scope="col">{k('colOne')}</th>
            <th scope="col">{k('colCombined')}</th>
          </tr>
        </thead>
        <tbody>
          {AI_ANALYSIS.bands.map((band) => (
            <tr key={band.band} data-band={band.band}>
              <th scope="row">{fillPricing(k('upTo'), locale, { count: formatCount(band.maxInputTokens, locale) })}</th>
              <td>{credits(band.creditsPerAction)}</td>
              <td>{credits(band.interpretationReviewCredits)}</td>
            </tr>
          ))}
          <tr data-band="split">
            <th scope="row">{fillPricing(k('over'), locale, { count: formatCount(largest.maxInputTokens, locale) })}</th>
            <td colSpan={2}>{k('split')}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CreditPackTable({ locale }) {
  const k = (key) => t(`pricing.credits.${key}`);
  return (
    <div className="sw-pricing-table-wrap" role="region" aria-label={k('packsCaption')} tabIndex={0}>
      <table className="sw-pricing-table" data-pricing-table="packs">
        <caption>{k('packsCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{k('colPack')}</th>
            <th scope="col">{k('colPrice')}</th>
            <th scope="col">{k('colExpiry')}</th>
          </tr>
        </thead>
        <tbody>
          {CREDIT_PACKS.map((pack) => (
            <tr key={pack.pack} data-pack={pack.pack}>
              <th scope="row">{fillPricing(k('creditsValue'), locale, { count: formatCount(pack.credits, locale) })}</th>
              <td>{formatUsd(pack.priceUsdCents, locale)}</td>
              <td>{k('never')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PricingPage() {
  const locale = getActiveLocale();
  const fill = (key) => fillPricing(t(key), locale);
  return (
    <PageShell
      currentPath="/pricing"
      title={t('meta.pricing.title')}
      description={t('meta.pricing.description')}
    >
      <PageHero
        showcase
        overlapNav
        eyebrow={t('pricing.hero.eyebrow')}
        title={<><span className="sw-hero-title-accent">{t('pricing.hero.titleAccent')}</span>{t('pricing.hero.titleRest')}</>}
        lead={fill('pricing.hero.lead')}
      >
        <a href={appUrl('/sign-up')} data-cta="pricing-hero-trial" className="sw-btn sw-btn-primary sw-btn-lg">
          <span className="sw-btn-label">{fill('pricing.hero.startTrial')}</span>
          {ARROW_ICON}
        </a>
        <a href="#sw-pricing-plans" className="sw-btn sw-btn-ghost-dark sw-btn-lg">{t('pricing.hero.downloadFirst')}</a>
      </PageHero>

      <section className="sw-section sw-section-default" aria-labelledby="sw-pricing-plans">
        <div className="sw-container">
          <h2 className="sw-pricing-sr-only" id="sw-pricing-plans">{t('pricing.plansLabel')}</h2>
          <div className="sw-pricing-grid sw-pricing-grid--plans">
            {CURRENT_PLANS.map((plan) => <PlanCard key={plan.plan} plan={plan} locale={locale} />)}
          </div>
          <p className="sw-pricing-foot-note">{fill('pricing.footNote')}</p>
        </div>
      </section>

      <section className="sw-section sw-section-soft" aria-labelledby="sw-pricing-compare">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-eyebrow sw-eyebrow-blue">{t('pricing.compare.eyebrow')}</div>
            <h2 className="sw-h2" id="sw-pricing-compare">{t('pricing.compare.title')}</h2>
          </div>
          <PlanComparisonTable locale={locale} />
          <p className="sw-pricing-foot-note">{t('pricing.compare.noSeats')}</p>
          <p className="sw-pricing-foot-note">{t('pricing.compare.sameMethods')}</p>
        </div>
      </section>

      <section className="sw-section sw-section-default" aria-labelledby="sw-pricing-trial">
        <div className="sw-container sw-pricing-split">
          <div>
            <div className="sw-eyebrow sw-eyebrow-blue">{t('pricing.trial.eyebrow')}</div>
            <h2 className="sw-h2" id="sw-pricing-trial">{fill('pricing.trial.title')}</h2>
            <a href={appUrl('/sign-up')} data-cta="pricing-trial" className="sw-btn sw-btn-primary sw-pricing-split-cta">
              <span className="sw-btn-label">{fill('pricing.trial.cta')}</span>
              {ARROW_ICON}
            </a>
          </div>
          <ul className="sw-pricing-points">
            {t('pricing.trial.points').map((point) => <li key={point}>{CHECK_ICON}<span>{fillPricing(point, locale)}</span></li>)}
          </ul>
        </div>
      </section>

      <section className="sw-section sw-section-soft" aria-labelledby="sw-pricing-credits">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-eyebrow sw-eyebrow-blue">{t('pricing.credits.eyebrow')}</div>
            <h2 className="sw-h2" id="sw-pricing-credits">{t('pricing.credits.title')}</h2>
            <p className="sw-lead" style={{ marginTop: 12 }}>{t('pricing.credits.lead')}</p>
          </div>
          <CreditPriceTable locale={locale} />
          <p className="sw-pricing-foot-note" data-availability="optional">{t('pricing.credits.preview')}</p>
          <ul className="sw-pricing-points sw-pricing-points--wide">
            {t('pricing.credits.points').map((point) => <li key={point}>{CHECK_ICON}<span>{fillPricing(point, locale)}</span></li>)}
          </ul>
          <h3 className="sw-pricing-subhead">{t('pricing.credits.packsTitle')}</h3>
          <CreditPackTable locale={locale} />
          <p className="sw-pricing-foot-note">{t('pricing.credits.packNote')}</p>
          <p className="sw-pricing-foot-note" data-policy="automatic-spending">{t('pricing.credits.automation')}</p>
        </div>
      </section>

      <section className="sw-section sw-section-default" aria-labelledby="sw-pricing-claims">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-eyebrow sw-eyebrow-blue">{t('pricing.claims.eyebrow')}</div>
            <h2 className="sw-h2" id="sw-pricing-claims">{t('pricing.claims.title')}</h2>
          </div>
          <div className="sw-card-grid-3">
            {t('pricing.claims.cards').map((card) => (
              <div className="sw-card" key={card.h}>
                <h3 className="sw-card-title">{card.h}</h3>
                <p className="sw-card-body">{card.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-soft" aria-labelledby="sw-pricing-other">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-eyebrow sw-eyebrow-blue">{t('pricing.other.eyebrow')}</div>
            <h2 className="sw-h2" id="sw-pricing-other">{t('pricing.other.title')}</h2>
          </div>
          <div className="sw-pricing-grid sw-pricing-grid--offers">
            <OfferCard id="pilot" text={t('pricing.other.pilot')} href={PILOT_CTA_HREF} price={PILOT_PRICE} />
            <OfferCard id="custom" text={t('pricing.other.custom')} href={CUSTOM_CTA_HREF} price={t('pricing.other.custom').price} />
          </div>
          <p className="sw-pricing-foot-note">{t('pricing.other.note')}</p>
        </div>
      </section>

      <PageCta
        source="pricing"
        title={t('pricing.cta.title')}
        lead={fill('pricing.cta.lead')}
        secondary={{ href: '/contact', label: t('pricing.cta.secondary') }}
      />
    </PageShell>
  );
}

// =============================================================
// 6. Early Access
// =============================================================
const EA_COMPANY_TYPES = [
  'Software agency', 'Custom software consultancy', 'Digital product studio',
  'Nearshore/offshore development company', 'Internal engineering team', 'Other',
];
const EA_COMPANY_SIZES = ['1 to 10', '11 to 25', '26 to 50', '51 to 100', '101 to 300', '301 to 500', '500+'];
const EA_ACTIVE_PROJECTS = ['1 to 3', '4 to 10', '11 to 25', '26 to 50', '50+', 'Not applicable'];
const EA_TOOLS = [
  'Jira', 'GitHub', 'GitLab', 'Bitbucket', 'Linear', 'Azure DevOps',
  'Google Calendar', 'Microsoft 365 Calendar', 'Slack', 'Microsoft Teams',
  'GitHub Copilot', 'Cursor', 'OpenAI Codex', 'Claude Code', 'Other',
];
const EA_PAINS = [
  'Proving delivery value to clients',
  'Explaining invoices or timelines',
  'Tracking scope creep',
  'Reducing manual client reporting',
  'Understanding project profitability',
  'Measuring AI delivery impact',
  'Understanding engineering cost',
  'Other',
];
const EA_YN = ['Yes', 'Maybe', 'No'];

// Mirrors STORAGE_KEY in scripts/OnboardingApp.jsx — the sample-report
// onboarding persists its answers under this key and the early-access form
// reads them to pre-fill, so an onboarded user doesn't retype the same fields.
const ONBOARDING_STORAGE_KEY = 'sw-onboarding';

// Maps the onboarding form shape (from OnboardingApp.jsx) onto the
// early-access form shape. Field names that don't exist on the EA form
// (helpWith, aiQuestion, designPartner, consent) are dropped; `notes` is
// folded into `message`. Returning an empty object means nothing pre-fills.
function readOnboardingPrefill() {
  if (typeof localStorage === 'undefined') return {};
  let raw = null;
  try { raw = localStorage.getItem(ONBOARDING_STORAGE_KEY); } catch (_) { return {}; }
  if (!raw) return {};
  let saved = null;
  try { saved = JSON.parse(raw); } catch (_) { return {}; }
  if (!saved || typeof saved !== 'object') return {};
  const out = {};
  if (saved.fullName)        out.fullName        = saved.fullName;
  if (saved.workEmail)       out.workEmail       = saved.workEmail;
  if (saved.companyName)     out.companyName     = saved.companyName;
  if (saved.companyWebsite)  out.companyWebsite  = saved.companyWebsite;
  if (saved.companyType)     out.companyType     = saved.companyType;
  if (saved.companySize)     out.companySize     = saved.companySize;
  if (saved.projectsCount)   out.activeProjects  = saved.projectsCount;
  if (Array.isArray(saved.tools) && saved.tools.length) out.tools = saved.tools;
  if (saved.pain)            out.biggestPain     = saved.pain;
  if (saved.pilotPriceFit)   out.pilotPriceFit   = saved.pilotPriceFit;
  if (saved.subscriptionFit) out.subscriptionFit = saved.subscriptionFit;
  if (saved.notes)           out.message         = saved.notes;
  return out;
}

export function EarlyAccessPage() {
  const [form, setForm] = React.useState(() => ({
    fullName: '', workEmail: '', companyName: '', companyWebsite: '',
    companyType: '', companySize: '', activeProjects: '', tools: [],
    biggestPain: '', shareData: '', paidPilot: '',
    pilotPriceFit: '', subscriptionFit: '', message: '',
    turnstileToken: '',
    ...readOnboardingPrefill(),
  }));
  const [prefilledFromOnboarding] = React.useState(() => Object.keys(readOnboardingPrefill()).length > 0);
  const [errors, setErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };
  const toggleTool = (t) => {
    setForm(f => ({ ...f, tools: f.tools.includes(t) ? f.tools.filter(x => x !== t) : [...f.tools, t] }));
    if (errors.tools) setErrors(e => ({ ...e, tools: null }));
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = t('earlyAccess.err.fullName');
    if (!form.workEmail.trim()) errs.workEmail = t('earlyAccess.err.workEmail');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.workEmail)) errs.workEmail = t('earlyAccess.err.email');
    if (!form.companyName.trim()) errs.companyName = t('earlyAccess.err.companyName');
    if (!form.companyWebsite.trim()) errs.companyWebsite = t('earlyAccess.err.companyWebsite');
    else if (!/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}/i.test(form.companyWebsite)) errs.companyWebsite = t('earlyAccess.err.url');
    if (!form.companyType) errs.companyType = t('earlyAccess.err.companyType');
    if (!form.companySize) errs.companySize = t('earlyAccess.err.companySize');
    if (!form.activeProjects) errs.activeProjects = t('earlyAccess.err.activeProjects');
    if (!form.tools.length) errs.tools = t('earlyAccess.err.tools');
    if (!form.biggestPain) errs.biggestPain = t('earlyAccess.err.biggestPain');
    if (!form.shareData) errs.shareData = t('earlyAccess.err.option');
    if (!form.paidPilot) errs.paidPilot = t('earlyAccess.err.option');
    if (!form.pilotPriceFit) errs.pilotPriceFit = t('earlyAccess.err.option');
    if (isTurnstileEnabled() && !form.turnstileToken) errs.turnstileToken = t('earlyAccess.err.verify');
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setSubmitError(null);

    // Tag the submission so we can tell onboarding-originated applications
    // from cold ones in the beta_testers table (functions/api/early-access.js).
    const source = (() => {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get('from') === 'onboarding'
          ? 'onboarding-handoff'
          : 'early-access-page';
      } catch (_) { return 'early-access-page'; }
    })();

    const handoff = (typeof window !== 'undefined' && typeof window.onEarlyAccessSubmit === 'function')
      ? window.onEarlyAccessSubmit({ ...form, source })
      : Promise.reject(new Error('Submission handler unavailable.'));

    Promise.resolve(handoff)
      .then(() => { setSubmitted(true); })
      .catch((err) => {
        const fields = (err && err.fields) || null;
        if (fields && typeof fields === 'object') setErrors((prev) => ({ ...prev, ...fields }));
        setSubmitError((err && err.message) || 'Something went wrong. Please try again.');
      })
      .finally(() => { setSubmitting(false); });
  };

  return (
    <PageShell
      currentPath="/early-access"
      title={t('meta.early-access.title')}
      description={t('meta.early-access.description')}
    >
      <div className="sw-page-early-access">
      <PageHero
        showcase
        overlapNav
        eyebrow={t('earlyAccess.hero.eyebrow')}
        title={<>{t('earlyAccess.hero.titlePre')}<span className="sw-hero-title-accent">{t('earlyAccess.hero.titleAccent')}</span>{t('earlyAccess.hero.titlePost')}</>}
        lead={t('earlyAccess.hero.lead')}
        visual={<EarlyAccessStackHeroVisual />}
      />

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-card-grid-2">
            {t('earlyAccess.cards').map((c, i) => (
              <div className="sw-card" key={i}>
                <div className="sw-card-eyebrow">{c.eyebrow}</div>
                <h3 className="sw-card-title">{c.title}</h3>
                <p className="sw-card-body">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-soft">
        <div className="sw-container">
          <div className="sw-section-narrow">
            <div className="sw-section-head sw-section-head-sm">
              <div className="sw-eyebrow sw-eyebrow-blue">{t('earlyAccess.apply.eyebrow')}</div>
              <h2 className="sw-h2">{t('earlyAccess.apply.title')}</h2>
              <p className="sw-lead" style={{ marginTop: 12 }}>
                {t('earlyAccess.apply.lead')}
              </p>
            </div>
            <div className="sw-page-form">
              {submitted ? (
                <EarlyAccessSuccess />
              ) : (
                <form className="sw-form sw-form--grouped" onSubmit={submit} noValidate>
                  {prefilledFromOnboarding && (
                    <div className="sw-form-prefill-notice" role="status">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M3.5 8.4L6.5 11.4L12.5 4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{t('earlyAccess.prefillNotice')}</span>
                    </div>
                  )}
                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('earlyAccess.groupAbout')}</legend>
                    <div className="sw-form-row">
                      <Field label={t('earlyAccess.fullName')} required error={errors.fullName} htmlFor="ea-name">
                        <input id="ea-name" className="sw-input" autoComplete="name"
                          aria-invalid={!!errors.fullName}
                          value={form.fullName} placeholder={t('earlyAccess.fullNamePh')}
                          onChange={e => set('fullName', e.target.value)}/>
                      </Field>
                      <Field label={t('earlyAccess.workEmail')} required error={errors.workEmail} htmlFor="ea-email">
                        <input id="ea-email" className="sw-input" type="email" autoComplete="email"
                          aria-invalid={!!errors.workEmail}
                          value={form.workEmail} placeholder={t('earlyAccess.workEmailPh')}
                          onChange={e => set('workEmail', e.target.value)}/>
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('earlyAccess.groupCompany')}</legend>
                    <div className="sw-form-row">
                      <Field label={t('earlyAccess.companyName')} required error={errors.companyName} htmlFor="ea-company">
                        <input id="ea-company" className="sw-input" autoComplete="organization"
                          aria-invalid={!!errors.companyName}
                          value={form.companyName} placeholder={t('earlyAccess.companyNamePh')}
                          onChange={e => set('companyName', e.target.value)}/>
                      </Field>
                      <Field label={t('earlyAccess.companyWebsite')} required error={errors.companyWebsite} htmlFor="ea-website">
                        <input id="ea-website" className="sw-input" type="url" autoComplete="url"
                          aria-invalid={!!errors.companyWebsite}
                          value={form.companyWebsite} placeholder={t('earlyAccess.companyWebsitePh')}
                          onChange={e => set('companyWebsite', e.target.value)}/>
                      </Field>
                    </div>
                    <div className="sw-form-row">
                      <Field label={t('earlyAccess.companyType')} required error={errors.companyType} htmlFor="ea-type">
                        <select id="ea-type" className="sw-input sw-select"
                          aria-invalid={!!errors.companyType}
                          value={form.companyType} onChange={e => set('companyType', e.target.value)}>
                          <option value="">{t('earlyAccess.selectOne')}</option>
                          {EA_COMPANY_TYPES.map((opt, i) => <option key={opt} value={opt}>{t('earlyAccess.companyTypes')[i]}</option>)}
                        </select>
                      </Field>
                      <Field label={t('earlyAccess.companySize')} required error={errors.companySize} htmlFor="ea-size">
                        <select id="ea-size" className="sw-input sw-select"
                          aria-invalid={!!errors.companySize}
                          value={form.companySize} onChange={e => set('companySize', e.target.value)}>
                          <option value="">{t('earlyAccess.selectOne')}</option>
                          {EA_COMPANY_SIZES.map((opt, i) => <option key={opt} value={opt}>{t('earlyAccess.companySizes')[i]}</option>)}
                        </select>
                      </Field>
                    </div>
                  </fieldset>

                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('earlyAccess.groupDelivery')}</legend>
                    <Field label={t('earlyAccess.activeProjects')} required error={errors.activeProjects} htmlFor="ea-projects">
                      <select id="ea-projects" className="sw-input sw-select"
                        aria-invalid={!!errors.activeProjects}
                        value={form.activeProjects} onChange={e => set('activeProjects', e.target.value)}>
                        <option value="">{t('earlyAccess.selectOne')}</option>
                        {EA_ACTIVE_PROJECTS.map((opt, i) => <option key={opt} value={opt}>{t('earlyAccess.activeProjectsOpts')[i]}</option>)}
                      </select>
                    </Field>
                    <FieldGroup label={t('earlyAccess.toolsLabel')} required error={errors.tools}
                      hint={t('earlyAccess.toolsHint')}>
                      <div className="sw-checkbox-grid" role="group" aria-label={t('earlyAccess.toolsLabel')}>
                        {EA_TOOLS.map(opt => {
                          const on = form.tools.includes(opt);
                          return (
                            <label key={opt} className={`sw-checkbox-item ${on ? 'is-on' : ''}`}>
                              <input type="checkbox" checked={on} onChange={() => toggleTool(opt)}/>
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FieldGroup>
                  </fieldset>

                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('earlyAccess.groupReporting')}</legend>
                    <Field label={t('earlyAccess.painLabel')} required error={errors.biggestPain} htmlFor="ea-pain">
                      <select id="ea-pain" className="sw-input sw-select"
                        aria-invalid={!!errors.biggestPain}
                        value={form.biggestPain} onChange={e => set('biggestPain', e.target.value)}>
                        <option value="">{t('earlyAccess.selectOne')}</option>
                        {EA_PAINS.map((opt, i) => <option key={opt} value={opt}>{t('earlyAccess.pains')[i]}</option>)}
                      </select>
                    </Field>
                    <FieldGroup label={t('earlyAccess.shareDataLabel')} required error={errors.shareData}>
                      <div className="sw-radio-row" role="radiogroup" aria-label={t('earlyAccess.shareDataLabel')}>
                        {EA_YN.map((opt, i) => {
                          const on = form.shareData === opt;
                          return (
                            <label key={opt} className={`sw-radio-item ${on ? 'is-on' : ''}`}>
                              <input type="radio" name="shareData" checked={on}
                                onChange={() => set('shareData', opt)}/>
                              <span>{t('earlyAccess.yn')[i]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FieldGroup>
                    <FieldGroup label={t('earlyAccess.paidPilotLabel')} required error={errors.paidPilot}>
                      <div className="sw-radio-row" role="radiogroup" aria-label={t('earlyAccess.paidPilotLabel')}>
                        {EA_YN.map((opt, i) => {
                          const on = form.paidPilot === opt;
                          return (
                            <label key={opt} className={`sw-radio-item ${on ? 'is-on' : ''}`}>
                              <input type="radio" name="paidPilot" checked={on}
                                onChange={() => set('paidPilot', opt)}/>
                              <span>{t('earlyAccess.yn')[i]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FieldGroup>
                    <Field label={t('earlyAccess.messageLabel')} htmlFor="ea-message"
                      hint={t('earlyAccess.messageHint')}>
                      <textarea id="ea-message" className="sw-input sw-textarea" rows={4}
                        value={form.message}
                        placeholder={t('earlyAccess.messagePh')}
                        onChange={e => set('message', e.target.value)}/>
                    </Field>
                  </fieldset>

                  <fieldset className="sw-form-group">
                    <legend className="sw-form-group-title">{t('earlyAccess.groupPricing')}</legend>
                    <p className="sw-form-group-note">
                      {fillPricing(t('earlyAccess.pricingNote'), getActiveLocale())}
                    </p>
                    <FieldGroup label={t('earlyAccess.pilotQuestion', { pilot: PILOT_PRICE })} required error={errors.pilotPriceFit}>
                      <div className="sw-radio-row sw-radio-row--wrap" role="radiogroup" aria-label={t('earlyAccess.pilotQuestion', { pilot: PILOT_PRICE })}>
                        {PILOT_PRICE_FIT_OPTIONS.map((opt, i) => {
                          const on = form.pilotPriceFit === opt;
                          return (
                            <label key={opt} className={`sw-radio-item ${on ? 'is-on' : ''}`}>
                              <input type="radio" name="pilotPriceFit" checked={on}
                                onChange={() => set('pilotPriceFit', opt)}/>
                              <span>{t('earlyAccess.pilotOpts')[i]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FieldGroup>
                    <FieldGroup label={t('earlyAccess.subQuestion')} hint={t('earlyAccess.subHint')}>
                      <div className="sw-radio-row sw-radio-row--wrap" role="radiogroup" aria-label={t('earlyAccess.subQuestion')}>
                        {SUBSCRIPTION_FIT_OPTIONS.map((opt, i) => {
                          const on = form.subscriptionFit === opt;
                          return (
                            <label key={opt} className={`sw-radio-item ${on ? 'is-on' : ''}`}>
                              <input type="radio" name="subscriptionFit" checked={on}
                                onChange={() => set('subscriptionFit', opt)}/>
                              <span>{fillPricing(t('earlyAccess.subOpts')[i], getActiveLocale())}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FieldGroup>
                  </fieldset>

                  <fieldset className="sw-form-group sw-form-group--final">
                    {isTurnstileEnabled() ? (
                      <div className="sw-turnstile-wrap">
                        <TurnstileWidget
                          action="early_access"
                          onToken={(token) => set('turnstileToken', token || '')}
                        />
                        {errors.turnstileToken ? (
                          <div className="sw-field-error sw-field-error--block">
                            {errors.turnstileToken}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {submitError ? (
                      <div className="sw-field-error sw-field-error--block" role="alert" style={{ marginBottom: 12 }}>
                        {submitError}
                      </div>
                    ) : null}
                    <div className="sw-form-foot sw-form-foot--end">
                      <button type="submit" className="sw-btn sw-btn-primary" disabled={submitting}>
                        <span className="sw-btn-label">{submitting ? t('earlyAccess.sending') : t('earlyAccess.submit')}</span>
                      </button>
                    </div>
                  </fieldset>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      </div>
    </PageShell>
  );
}

function EarlyAccessSuccess() {
  return (
    <div className="sw-page-form-success">
      <div className="sw-success-mark" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7"/></svg>
      </div>
      <h3 className="sw-success-title">{t('earlyAccess.success.title')}</h3>
      <p className="sw-success-line">{t('earlyAccess.success.line1')}</p>
      <p className="sw-success-line sw-success-meta">{t('earlyAccess.success.line2')}</p>
      <a href={localizedHref('/')} className="sw-btn sw-btn-ghost"><span className="sw-btn-label">{t('earlyAccess.success.back')}</span></a>
    </div>
  );
}

// =============================================================
// 7. Sample Report
// =============================================================

export function SampleReportPage() {
  return (
    <PageShell
      currentPath="/sample-report"
      title="Sample report | ScopeWorth"
      description="Preview a sample ScopeWorth SDLC ROI report showing delivery value, scope expansion, cost, rework, and AI delivery impact."
    >
      <PageHero
        showcase
        overlapNav
        eyebrow={t('sampleReport.hero.eyebrow')}
        title={<>{t('sampleReport.hero.titlePre')}<span className="sw-hero-title-accent">{t('sampleReport.hero.titleAccent')}</span>{t('sampleReport.hero.titleRest')}</>}
        lead={t('sampleReport.hero.lead')}
        visual={<SampleReportHeroVisual />}
      >
        {/* Shorter than the shared `cta.downloadSample` label on purpose: the
            showcase support column is narrow, and the full "…in 2 minutes"
            wording pushed "See pricing" onto a second row. */}
        <SampleReportButton source="sample-report-page-hero" label={t('sampleReport.hero.downloadSample')} />
        <a href={localizedHref('/pricing')} className="sw-btn sw-btn-ghost-dark sw-btn-lg"><span className="sw-btn-label">{t('sampleReport.hero.seePricing')}</span></a>
      </PageHero>

      {/* The page's only section, and its only job: send the reader into
          /sample-report/start. It is the shared final-CTA composition on a light
          surface — the hero above it is dark, so the page closes by opening
          up rather than by stacking a second dark block. Written here rather
          than through <PageCta> because the copy carries a cost line the
          shared component has no slot for, and that line is the argument. */}
      <section className="sw-section sw-section-final sw-section-final-light" id="cta">
        <div className="sw-container">
          <div className="sw-final">
            <div className="sw-eyebrow sw-eyebrow-teal">{t('sampleReport.cta.eyebrow')}</div>
            <h2 className="sw-h-display">{t('sampleReport.cta.title')}</h2>
            <p className="sw-lead">{t('sampleReport.cta.lead')}</p>
            <div className="sw-cta-actions">
              <SampleReportButton source="sample-report-page-cta" label={t('sampleReport.cta.action')} />
              <a href={localizedHref('/contact')} className="sw-btn sw-btn-ghost sw-btn-lg">
                <span className="sw-btn-label">{t('cta.contactUs')}</span>
              </a>
            </div>
            <p className="sw-final-note">{t('sampleReport.cta.note')}</p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function IntegIconFourTiles() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="22" height="22" rx="4"/>
      <rect x="31" y="3" width="22" height="22" rx="4"/>
      <rect x="3" y="31" width="22" height="22" rx="4"/>
      <rect x="31" y="31" width="22" height="22" rx="4"/>
      <path d="M25 14h6M25 42h6M14 25v6M42 25v6"/>
    </svg>
  );
}

function IntegIconConstellation() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="28" cy="28" r="4"/>
      <circle cx="8" cy="10" r="3"/>
      <circle cx="48" cy="10" r="3"/>
      <circle cx="7" cy="46" r="3"/>
      <circle cx="49" cy="46" r="3"/>
      <circle cx="28" cy="5" r="3"/>
      <circle cx="53" cy="28" r="3"/>
      <path d="M25 25.5L11 13M31 25.5L45 13M25 30.5L10 43M31 30.5L46 43M28 24V8M32 28L50 28"/>
    </svg>
  );
}

function IntegIconShieldList() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M28 4L8 12v14c0 13 9 22 20 26 11-4 20-13 20-26V12L28 4Z"/>
      <path d="M19 25l5 5 13-13"/>
      <path d="M19 37h10"/>
    </svg>
  );
}

function providerHelp(integ) {
  const key = `integrations.providerHelp.${integ.id}`;
  const help = t(key);
  return help === key ? t('integrations.limitedHelp') : help;
}

function IntegrationCard({ name }) {
  const integ = getCatalogIntegration(name);
  if (!integ) return null;
  const isAvailable = integ.availability === 'limited_setup';
  const modeLabel = isAvailable ? t(`integrations.modes.${integ.mode}`) : null;
  return (
    <div className="sw-integration sw-integration--logo sw-reveal">
      <div className="sw-integration-h">
        <div className="sw-integration-h-left">
          <IntegrationLogo name={name} size={32} />
          <span className="sw-integration-name">{name}</span>
        </div>
        <span className={`sw-integration-tag ${isAvailable ? 'is-available' : 'is-roadmap'}`}>
          {isAvailable ? t('integrations.tagLimited') : t('integrations.tagRoadmap')}
        </span>
      </div>
      <div className="sw-integration-meta">
        <span className="sw-integration-cat">{categoryLabel(integ.category)}</span>
        {modeLabel ? <span className="sw-integration-mode">{modeLabel}</span> : null}
      </div>
      {isAvailable ? <p className="sw-card-body">{providerHelp(integ)}</p> : null}
      {isAvailable ? <a className="sw-textlink" href={appUrl('/sign-up')} data-cta="integration-setup">{t('integrations.setUp')}</a> : null}
    </div>
  );
}

function categoryLabel(category) {
  const key = 'integrations.categories.' + category;
  const label = t(key);
  return label === key ? category : label;
}

function RegionalPopularSection() {
  const { region } = useRegion();
  const copy = t('integrations.regional.' + region);
  const names = REGIONAL_LOGO_CLUSTERS[region] || [];
  return (
    <section className="sw-section sw-section-default">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-sm">
          <div className="sw-section-head-row">
            <div>
              <div className="sw-eyebrow sw-eyebrow-blue">{t('integrations.regionalPopularEyebrow')}</div>
              <h2 className="sw-h2">{copy.title}</h2>
              <p className="sw-lead" style={{ marginTop: 12 }}>{copy.lead}</p>
            </div>
            <div className="sw-section-head-icon">
              <IntegIconConstellation />
            </div>
          </div>
        </div>
        <div className="sw-region-popular-grid">
          {names.map(n => {
            const integ = getCatalogIntegration(n);
            if (!integ) return null;
            return (
              <div key={n} className="sw-region-popular-card sw-reveal">
                <IntegrationLogo name={n} size={36} />
                <div className="sw-region-popular-name">{n}</div>
                <div className="sw-region-popular-cat">{categoryLabel(integ.category)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RegionalRoadmapSection() {
  const { region } = useRegion();
  const groups = t('integrations.roadmap.groups');
  return (
    <section className="sw-section sw-section-soft">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-sm">
          <div className="sw-section-head-row">
            <div>
              <div className="sw-eyebrow sw-eyebrow-teal">{t('integrations.roadmap.eyebrow')}</div>
              <h2 className="sw-h2">{t('integrations.roadmap.title')}</h2>
              <p className="sw-lead" style={{ marginTop: 12 }}>{t('integrations.roadmap.summary')}</p>
              <p className="sw-roadmap-summary" style={{ marginTop: 12 }}>{t('integrations.roadmap.emphasis.' + region)}</p>
            </div>
            <div className="sw-section-head-icon sw-section-head-icon--teal">
              <IntegIconShieldList />
            </div>
          </div>
        </div>
        <div className="sw-roadmap-grid">
          {ROADMAP_GROUPS.map(group => (
            <div key={group.id} className="sw-roadmap-card sw-reveal">
              <div><span className="sw-integration-tag is-roadmap">{t('integrations.tagRoadmap')}</span></div>
              <h3 className="sw-roadmap-title">{groups[group.id]?.title}</h3>
              <p className="sw-card-body">{groups[group.id]?.hint}</p>
              <div className="sw-roadmap-vendors">
                {group.names.map(v => (
                  <span key={v} className="sw-roadmap-vendor">
                    <IntegrationLogo name={v} size={20} />
                    <span>{v}</span>
                  </span>
                ))}
              </div>
              <a className="sw-textlink" href={localizedHref('/contact')}>{t('integrations.cta.secondary')}</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IntegrationsPage() {
  return (
    <PageShell
      currentPath="/integrations"
      title={t('meta.integrations.title')}
      description={t('meta.integrations.description')}
    >
      <div className="sw-page-integrations">
      <PageHero
        showcase
        overlapNav
        eyebrow={t('integrations.hero.eyebrow')}
        title={<>{t('integrations.hero.titlePre')}<span className="sw-hero-title-accent">{t('integrations.hero.titleAccent')}</span>{t('integrations.hero.titlePost')}</>}
        lead={t('integrations.hero.lead')}
        visual={<IntegrationsToolsHeroVisual />}
      />

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-section-head-row">
              <div>
              <div className="sw-eyebrow sw-eyebrow-blue">{t('integrations.available.eyebrow')}</div>
                <h2 className="sw-h2">{t('integrations.available.title')}</h2>
                <p className="sw-lead" style={{ marginTop: 12 }}>
                   {t('integrations.available.lead')}
                </p>
              </div>
              <div className="sw-section-head-icon">
                <IntegIconFourTiles />
              </div>
            </div>
          </div>
          <div className="sw-integration-grid">
            {LIMITED_SETUP_NAMES.map(name => (
              <IntegrationCard key={name} name={name} />
            ))}
          </div>
        </div>
      </section>

      <section className="sw-section sw-section-soft">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-section-head-row">
              <div>
                <div className="sw-eyebrow sw-eyebrow-teal">{t('integrations.import.eyebrow')}</div>
                <h2 className="sw-h2">{t('integrations.import.title')}</h2>
                <p className="sw-lead" style={{ marginTop: 12 }}>
                   {t('integrations.import.lead')}
                </p>
              </div>
              <div className="sw-section-head-icon sw-section-head-icon--teal">
                <IntegIconConstellation />
              </div>
            </div>
          </div>
          <div className="sw-import-capabilities" role="list">
            {IMPORT_CAPABILITY_IDS.map(id => (
              <div key={id} className="sw-import-capability" role="listitem">
                <span className="sw-import-capability-mark" aria-hidden="true">✓</span>
                <span>{t(`integrations.import.capabilities.${id}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="sw-reveal"><RegionalPopularSection /></div>

      <div className="sw-reveal"><RegionalRoadmapSection /></div>

      <section className="sw-section sw-section-soft">
        <div className="sw-container">
          <div className="sw-section-head sw-section-head-sm">
            <div className="sw-section-head-row">
              <div>
                <div className="sw-eyebrow sw-eyebrow-blue">{t('integrations.principlesHead.eyebrow')}</div>
                <h2 className="sw-h2">{t('integrations.principlesHead.title')}</h2>
              </div>
              <div className="sw-section-head-icon">
                <IntegIconShieldList />
              </div>
            </div>
          </div>
          <div className="sw-card-grid-2">
            {t('integrations.principles').map((p, i) => (
              <div key={i} className="sw-card">
                <h3 className="sw-card-title">{p.t}</h3>
                <p className="sw-card-body">{p.b}</p>
              </div>
            ))}
          </div>
          <p className="sw-trademark-disclaimer">{t('integrations.trademark')}</p>
        </div>
      </section>

      <PageCta
        source="integrations"
        title={t('integrations.cta.title')}
        lead={t('integrations.cta.lead')}
        secondary={{ label: t('integrations.cta.secondary'), href: '/contact' }}
      />
      </div>
    </PageShell>
  );
}

// =============================================================
// 9. Use Cases
// =============================================================

function UseCaseCard({ useCase, index }) {
  return (
    <article className="sw-usecase sw-reveal">
      <div className="sw-usecase-h">
        <span className="sw-usecase-num">{String(index + 1).padStart(2, '0')}</span>
        <h3 className="sw-usecase-title"><a className="sw-textlink" href={localizedHref(`/use-cases/${USE_CASE_SLUGS[index]}`)}>{useCase.t}</a></h3>
      </div>
      <div className="sw-usecase-row">
        <div className="sw-usecase-row-l">{t('useCases.rowLabels.moment')}</div>
        <div className="sw-usecase-row-r">{useCase.moment}</div>
      </div>
      <div className="sw-usecase-row">
        <div className="sw-usecase-row-l">{t('useCases.rowLabels.pain')}</div>
        <div className="sw-usecase-row-r">{useCase.pain}</div>
      </div>
      <div className="sw-usecase-row">
        <div className="sw-usecase-row-l">{t('useCases.rowLabels.output')}</div>
        <div className="sw-usecase-row-r"><em>{useCase.output}</em></div>
      </div>
      <div className="sw-usecase-row">
        <div className="sw-usecase-row-l">{t('useCases.rowLabels.action')}</div>
        <div className="sw-usecase-row-r"><strong>{useCase.action}</strong></div>
      </div>
    </article>
  );
}

export function UseCasesPage() {
  return (
      <PageShell
        currentPath="/use-cases"
        title={t('meta.use-cases.title')}
        description={t('meta.use-cases.description')}
      >
        <PageHero
          showcase
          overlapNav
          eyebrow={t('useCases.hero.eyebrow')}
          title={<>{t('useCases.hero.titlePre')}<span className="sw-hero-title-accent">{t('useCases.hero.titleAccent')}</span>{t('useCases.hero.titlePost')}</>}
          lead={t('useCases.hero.lead')}
          visual={<UseCasesEvidenceHeroVisual />}
        />

      <section className="sw-section sw-section-default">
        <div className="sw-container">
          <div className="sw-card-grid-2">
            {t('useCases.cases').map((u, i) => (
              <UseCaseCard key={i} useCase={u} index={i} />
            ))}
          </div>
        </div>
      </section>

      <PageCta
        source="use-cases"
        title={t('useCases.cta.title')}
        lead={t('useCases.cta.lead')}
        secondary={{ href: '/pricing', label: t('useCases.cta.secondary') }}
      />
    </PageShell>
  );
}

// =============================================================
// 10. Blog / Insights
// =============================================================
const BLOG_POST_HREF = (slug) => `/blog/${encodeURIComponent(slug)}/`;
const BLOG_POST_CANONICAL = (slug) => `https://scopeworth.app${BLOG_POST_HREF(slug)}`;

function getBlogSlugFromLocation() {
  if (typeof window === 'undefined') return null;
  const pathMatch = window.location.pathname.match(/^\/blog\/([^/]+)\/?$/i);
  if (pathMatch) {
    try { return decodeURIComponent(pathMatch[1]); } catch { return pathMatch[1]; }
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('post') || params.get('slug');
}
const BLOG_CATEGORY = {
  article: { label: 'Insights', tagClass: 'ins-tag-article' },
  release: { label: 'Release notes', tagClass: 'ins-tag-release' },
};
// ---- Atoms ----
function InsTag({ type }) {
  const safeType = BLOG_CATEGORY[type] ? type : 'article';
  return (
    <span className={`ins-tag ${BLOG_CATEGORY[safeType].tagClass}`}>
      <span className="ins-tag-dot" aria-hidden="true" />
      {t('blog.category.' + safeType)}
    </span>
  );
}

function InsVersion({ version }) {
  if (!version) return null;
  return <span className="ins-version tabular">{version}</span>;
}

function InsByline({ author, size }) {
  const a = AUTHORS[author] || AUTHORS.scopeworth;
  return (
    <span className={`ins-byline ${size === 'lg' ? 'ins-byline-lg' : ''}`}>
      <span className="ins-avatar" aria-hidden="true">{a.initials}</span>
      <span className="ins-byline-text">
        <span className="ins-byline-name">{a.name}</span>
        {a.role ? <span className="ins-byline-role">{a.role}</span> : null}
      </span>
    </span>
  );
}

// ---- Mock figures (illustrative; fictional / anonymized sample data) ----
function InsMock({ kind, compact }) {
  if (kind === 'waterfall') return <InsWaterfall compact={compact} />;
  if (kind === 'timeline') return <InsTimeline compact={compact} />;
  if (kind === 'ai') return <InsAiEconomics compact={compact} />;
  if (kind === 'gauge') return <InsGauge compact={compact} />;
  return null;
}

function InsWaterfall({ compact }) {
  const max = 38400;
  const bars = [
    { label: 'Feature build', v: 24600, disp: '$24.6k', tone: 'blue' },
    { label: 'Code review', v: 3600, disp: '$3.6k', tone: 'slate' },
    { label: 'Rework', v: 3200, disp: '$3.2k', tone: 'amber' },
    { label: 'Meetings', v: 5100, disp: '$5.1k', tone: 'slate' },
    { label: 'Other', v: 1900, disp: '$1.9k', tone: 'slate' },
  ];
  return (
    <figure className={`ins-mock ins-mock-waterfall ${compact ? 'is-compact' : ''}`}>
      <div className="ins-mock-head">
        <span className="ins-mock-title">Invoice defense</span>
        <span className="ins-mock-chip ins-chip-blue tabular">$38,400 billed</span>
      </div>
      <div className="ins-wf">
        <div className="ins-wf-plot">
          {bars.map((b) => (
            <div key={b.label} className="ins-wf-col">
              <div className="ins-wf-track">
                <span className={`ins-wf-bar tone-${b.tone}`} style={{ bottom: 0, height: `${(b.v / max) * 100}%` }}>
                  {!compact ? <span className="ins-wf-val tabular">{b.disp}</span> : null}
                </span>
              </div>
              <span className="ins-wf-label">{b.label}</span>
            </div>
          ))}
          <div className="ins-wf-col ins-wf-col-total">
            <div className="ins-wf-track">
              <span className="ins-wf-bar tone-total" style={{ bottom: 0, height: '100%' }}>
                {!compact ? <span className="ins-wf-val tabular">$38.4k</span> : null}
              </span>
            </div>
            <span className="ins-wf-label">Billed</span>
          </div>
        </div>
      </div>
    </figure>
  );
}

function InsTimeline({ compact }) {
  const baseline = [8, 28, 48, 70, 92];
  const adds = [
    { left: 18, cost: '+$600' },
    { left: 38, cost: '+$1,200' },
    { left: 56, cost: '+$900' },
    { left: 76, cost: '+$2,300' },
    { left: 90, cost: '+$1,800' },
  ];
  return (
    <figure className={`ins-mock ins-mock-timeline ${compact ? 'is-compact' : ''}`}>
      <div className="ins-mock-head">
        <span className="ins-mock-title">Scope creep timeline</span>
        <span className="ins-mock-chip ins-chip-amber tabular">+$6,800 expansion</span>
      </div>
      <div className="ins-tl">
        <div className="ins-tl-lane">
          <span className="ins-tl-lane-label">SOW baseline</span>
          <div className="ins-tl-axis">
            <span className="ins-tl-base-line" />
            {baseline.map((l) => (
              <span key={l} className="ins-tl-node tone-slate" style={{ left: `${l}%` }} />
            ))}
          </div>
        </div>
        <div className="ins-tl-lane">
          <span className="ins-tl-lane-label">Added after sign-off</span>
          <div className="ins-tl-axis">
            <span className="ins-tl-base-line is-dashed" />
            {adds.map((a) => (
              <span key={a.left} className="ins-tl-node tone-amber" style={{ left: `${a.left}%` }}>
                {!compact ? <span className="ins-tl-cost tabular">{a.cost}</span> : null}
              </span>
            ))}
          </div>
        </div>
        <div className="ins-tl-weeks">
          {['W1', 'W2', 'W3', 'W4'].map((w) => <span key={w} className="ins-tl-week">{w}</span>)}
        </div>
      </div>
    </figure>
  );
}

function InsAiEconomics({ compact }) {
  const rows = [
    { label: 'Implementation saved', val: '+21h', tone: 'teal', w: 100 },
    { label: 'Review overhead', val: '−4h', tone: 'amber', w: 19 },
  ];
  return (
    <figure className={`ins-mock ins-mock-ai ${compact ? 'is-compact' : ''}`}>
      <div className="ins-mock-head">
        <span className="ins-mock-title">AI economics</span>
        <span className="ins-mock-chip ins-chip-teal tabular">41% AI-assisted</span>
      </div>
      <div className="ins-ai">
        {rows.map((r) => (
          <div key={r.label} className="ins-ai-row">
            <span className="ins-ai-label">{r.label}</span>
            <div className="ins-ai-track"><span className={`ins-ai-bar tone-${r.tone}`} style={{ width: `${r.w}%` }} /></div>
            <span className={`ins-ai-val tabular tone-${r.tone}`}>{r.val}</span>
          </div>
        ))}
        <div className="ins-ai-row is-net">
          <span className="ins-ai-label">Net delivery gain</span>
          <div className="ins-ai-track"><span className="ins-ai-bar tone-blue" style={{ width: '81%' }} /></div>
          <span className="ins-ai-val tabular tone-blue">+17h</span>
        </div>
      </div>
    </figure>
  );
}

function InsGauge({ compact }) {
  const drivers = [
    { dir: 'up', text: '82% of PRs linked to Jira tickets' },
    { dir: 'up', text: '64% of tickets mapped to a project' },
    { dir: 'down', text: '39% of meetings tagged' },
  ];
  const len = Math.PI * 80; // semicircle arc length, r=80
  return (
    <figure className={`ins-mock ins-mock-gauge ${compact ? 'is-compact' : ''}`}>
      <div className="ins-mock-head">
        <span className="ins-mock-title">Report confidence</span>
        <span className="ins-mock-chip ins-chip-amber">Medium</span>
      </div>
      <div className="ins-gauge">
        <div className="ins-gauge-arc">
          <svg viewBox="0 0 180 100" width="100%" role="img" aria-label="74% confidence">
            <defs>
              <linearGradient id="insGaugeGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#2563EB" />
              </linearGradient>
            </defs>
            <path d="M10 90 A 80 80 0 0 1 170 90" fill="none" stroke="var(--slate-100)" strokeWidth="14" strokeLinecap="round" />
            <path d="M10 90 A 80 80 0 0 1 170 90" fill="none" stroke="url(#insGaugeGrad)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${len * 0.74} ${len}`} />
          </svg>
          <div className="ins-gauge-read">
            <span className="ins-gauge-num tabular">74%</span>
            <span className="ins-gauge-cap">grounded</span>
          </div>
        </div>
        {!compact ? (
          <ul className="ins-gauge-drivers">
            {drivers.map((d) => (
              <li key={d.text} className={`ins-driver ins-driver-${d.dir}`}>
                <span className="ins-driver-mark" aria-hidden="true">{d.dir === 'up' ? '↑' : '↓'}</span>
                {d.text}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </figure>
  );
}

// ---- Cards ----
function BlogCard({ post, variant }) {
  const featured = variant === 'featured';
  const isRelease = post.type === 'release';
  return (
    <a
      className={`ins-card ${featured ? 'ins-card-featured' : ''} ${isRelease ? 'ins-card-release' : ''}`}
      href={BLOG_POST_HREF(post.slug)}
    >
      {featured ? (
        <div className={`ins-card-visual ${post.cardImage ? 'ins-card-visual-media' : ''}`} aria-hidden="true">
          {post.cardImage ? (
            <img
              className="ins-card-visual-image"
              src={post.cardImage}
              alt=""
              width="1200"
              height="900"
            />
          ) : (
            <InsMock kind="waterfall" compact />
          )}
        </div>
      ) : null}
      <div className="ins-card-body">
        <div className="ins-card-tags">
          <InsTag type={post.type} />
          {isRelease ? <InsVersion version={post.version} /> : null}
        </div>
        <h3 className={`ins-card-title ${featured ? 'ins-card-title-lg' : ''}`}>{post.title}</h3>
        <p className="ins-card-deck">{post.deck}</p>
        <div className="ins-card-foot">
          <InsByline author={post.author} />
          <span className="ins-card-meta tabular">
            {formatBlogDate(post.date)}
            <span className="ins-dot-sep" aria-hidden="true" />
            {post.readMins ? `${post.readMins} min read` : 'Release'}
          </span>
        </div>
      </div>
    </a>
  );
}

function BlogChangelogRow({ post }) {
  const counts = { new: 0, improved: 0, fixed: 0 };
  (post.changes || []).forEach((g) => { counts[g.kind] = (counts[g.kind] || 0) + g.items.length; });
  return (
    <a className="ins-log" href={BLOG_POST_HREF(post.slug)}>
      <div className="ins-log-rail" aria-hidden="true">
        <InsVersion version={post.version} />
        <span className="ins-log-line" />
      </div>
      <div className="ins-log-body">
        <div className="ins-log-meta tabular">
          <InsTag type={post.type} />
          <span>{formatBlogDate(post.date)}</span>
        </div>
        <h3 className="ins-log-title">{post.title}</h3>
        <p className="ins-log-deck">{post.deck}</p>
        <div className="ins-log-counts">
          {counts.new ? <span className="ins-count ins-count-new"><span>{counts.new}</span> New</span> : null}
          {counts.improved ? <span className="ins-count ins-count-improved"><span>{counts.improved}</span> Improved</span> : null}
          {counts.fixed ? <span className="ins-count ins-count-fixed"><span>{counts.fixed}</span> Fixed</span> : null}
        </div>
      </div>
      <span className="ins-log-arrow" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
      </span>
    </a>
  );
}

export function BlogPage() {
  const [filter, setFilter] = React.useState('all'); // 'all' | 'article' | 'release'

  React.useEffect(() => {
    const f = new URLSearchParams(window.location.search).get('filter');
    if (f === 'article' || f === 'release') setFilter(f);
  }, []);

  const setAndSync = (f) => {
    setFilter(f);
    try {
      const url = new URL(window.location.href);
      if (f === 'all') url.searchParams.delete('filter');
      else url.searchParams.set('filter', f);
      window.history.replaceState({}, '', url);
    } catch { /* no-op */ }
  };

  const articles = BLOG_POSTS.filter((p) => p.type === 'article');
  const releases = BLOG_POSTS.filter((p) => p.type === 'release');
  const counts = { all: BLOG_POSTS.length, article: articles.length, release: releases.length };
  const isRelease = filter === 'release';
  const visible = filter === 'all' ? BLOG_POSTS : (filter === 'article' ? articles : releases);
  const featured = !isRelease ? articles.find((p) => p.featured) : null;
  const grid = featured ? visible.filter((p) => p.slug !== featured.slug) : visible;

  const tabs = [
    { key: 'all', label: t('blog.tabs.all') },
    { key: 'article', label: t('blog.tabs.article') },
    { key: 'release', label: t('blog.tabs.release') },
  ];

  return (
    <PageShell
      currentPath="/blog"
      title={t('meta.blog.title')}
      description={t('meta.blog.description')}
    >
      <PageHero
        showcase
        overlapNav
        eyebrow={t('blog.hero.eyebrow')}
        title={<>{t('blog.hero.titleL1')}<br/><span className="sw-hero-title-accent">{t('blog.hero.titleL2')}</span></>}
        lead={t('blog.hero.deck')}
        visual={<SavedReportsHeroVisual/>}
      />

      <section className="ins-list">
        <div className="sw-container">
          <div className="ins-list-bar">
            <div className="ins-filter" role="tablist" aria-label="Filter Insights">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === tab.key}
                  className={`ins-filter-btn ${filter === tab.key ? 'is-on' : ''} ${tab.key === 'release' ? 'ins-filter-release' : ''}`}
                  onClick={() => setAndSync(tab.key)}
                >
                  {tab.key === 'release' ? <span className="ins-filter-dot" aria-hidden="true" /> : null}
                  {tab.label}
                  <span className="ins-filter-count tabular">{counts[tab.key]}</span>
                </button>
              ))}
            </div>
            <span className="ins-list-note tabular">{isRelease ? t('blog.newestFirst') : t('blog.postsCount', { n: visible.length })}</span>
          </div>

          {isRelease ? (
            <div className="ins-changelog">
              {releases.map((p) => <BlogChangelogRow key={p.slug} post={p} />)}
            </div>
          ) : (
            <>
              {featured ? (
                <div className="ins-featured-wrap">
                  <BlogCard post={featured} variant="featured" />
                </div>
              ) : null}
              <div className="ins-grid">
                {grid.map((p) => <BlogCard key={p.slug} post={p} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}

// ---- Post body renderers ----
function BlogBlock({ block }) {
  switch (block.kind) {
    case 'lead':
      return <p className="ins-body-lead">{block.text}</p>;
    case 'h2':
      return <h2 id={block.id} className="ins-body-h2">{block.text}</h2>;
    case 'h3':
      return <h3 id={block.id} className="ins-body-h3">{block.text}</h3>;
    case 'p':
      return <p className="ins-body-p" dangerouslySetInnerHTML={{ __html: block.text }} />;
    case 'ul':
      return <ul className="ins-body-ul">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case 'ol':
      return <ol className="ins-body-ol">{block.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
    case 'quote':
      return (
        <blockquote className="ins-pullquote">
          <span className="ins-pullquote-mark" aria-hidden="true">&ldquo;</span>
          <p>{block.text}</p>
          {block.cite ? <cite>{block.cite}</cite> : null}
        </blockquote>
      );
    case 'metric':
      return (
        <div className="ins-metric" role="figure">
          <div className="ins-metric-value tabular">{block.value}</div>
          <div className="ins-metric-side">
            <div className="ins-metric-label">{block.label}</div>
            {block.sub ? <div className="ins-metric-sub">{block.sub}</div> : null}
          </div>
        </div>
      );
    case 'callout':
      return (
        <aside className="ins-callout">
          {block.label ? <strong className="ins-callout-label">{block.label}</strong> : null}
          <p>{block.text}</p>
        </aside>
      );
    case 'figure':
      return (
        <figure className="ins-figure">
          <div className="ins-figure-stage"><InsMock kind={block.mock} /></div>
          {block.caption ? <figcaption className="ins-figure-cap">{block.caption}</figcaption> : null}
        </figure>
      );
    default:
      return null;
  }
}

function BlogChangeGroup({ group }) {
  return (
    <section className={`ins-changes is-${group.kind}`}>
      <div className="ins-changes-head">
        <span className="ins-changes-marker" aria-hidden="true" />
        <h2 className="ins-changes-title">{t('blog.change.' + group.kind)}</h2>
        <span className="ins-changes-count tabular">{group.items.length}</span>
      </div>
      <ul className="ins-changes-list">
        {group.items.map((it, i) => (
          <li key={i} className="ins-change">
            {it.area ? <span className="ins-area-tag">{it.area}</span> : null}
            <span className="ins-change-text">{it.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BlogToc({ items }) {
  const [active, setActive] = React.useState(items[0] ? items[0].id : null);
  React.useEffect(() => {
    const els = items.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-96px 0px -65% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items]);
  if (!items.length) return null;
  return (
    <nav className="ins-toc" aria-label={t('blog.toc')}>
      <div className="ins-toc-h">{t('blog.toc')}</div>
      <ul>
        {items.map((t) => (
          <li key={t.id}>
            <a href={`#${t.id}`} className={active === t.id ? 'is-active' : ''}>{t.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function BlogSampleCta() {
  return (
    <section className="ins-cta">
      <div className="ins-cta-inner">
        <div className="ins-cta-bg" aria-hidden="true" />
        <div className="ins-cta-copy">
          <div className="sw-eyebrow sw-eyebrow-teal">{t('blog.sampleCta.eyebrow')}</div>
          <h2 className="ins-cta-title">{t('blog.sampleCta.title')}</h2>
          <p className="ins-cta-deck">
            {t('blog.sampleCta.deck')}
          </p>
        </div>
        <div className="ins-cta-actions">
          <button
            type="button"
            className="sw-btn sw-btn-primary sw-btn-lg"
            onClick={() => window.openSampleReport && window.openSampleReport('blog-post')}
          >
            <span className="sw-btn-label">{t('blog.sampleCta.download')}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
          <a className="sw-btn sw-btn-ghost-dark sw-btn-lg" href={localizedHref('/contact')}>
            <span className="sw-btn-label">{t('blog.sampleCta.book')}</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function BlogRelated({ slug }) {
  const related = getRelatedPosts(slug, 3);
  if (!related.length) return null;
  return (
    <section className="ins-related">
      <div className="sw-container">
        <div className="ins-related-head">
          <h2 className="ins-related-title">{t('blog.related.title')}</h2>
          <a className="ins-related-all" href={localizedHref('/blog')}>
            {t('blog.related.viewAll')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </a>
        </div>
        <div className="ins-grid">
          {related.map((p) => <BlogCard key={p.slug} post={p} />)}
        </div>
      </div>
    </section>
  );
}

// ---- Single post (article or release notes) ----
// `slug` is passed only by the build-time prerender, which has no URL to read
// the post from. In the browser it stays undefined and the location wins.
export function BlogPostPage({ slug: slugOverride } = {}) {
  const slug = slugOverride || getBlogSlugFromLocation();
  const post = getBlogPost(slug);

  React.useEffect(() => {
    if (!post || typeof window === 'undefined') return;
    if (/^\/blog-post\/?$/i.test(window.location.pathname)) {
      window.history.replaceState({}, '', BLOG_POST_HREF(post.slug));
    }
  }, [post]);

  if (!post) {
    return (
      <PageShell
        currentPath="/blog"
        title="Article not found | ScopeWorth"
        description="The requested ScopeWorth insight could not be found."
      >
        <section className="sw-section sw-section-default">
          <div className="sw-container sw-section-narrow">
            <div className="sw-eyebrow">Insights</div>
            <h1 className="sw-h2">Article not found</h1>
            <p className="sw-lead">This article may have moved or the URL may be incomplete.</p>
            <a className="sw-btn sw-btn-primary" href={localizedHref('/blog')}>View all insights</a>
          </div>
        </section>
      </PageShell>
    );
  }

  const isRelease = post.type === 'release';
  const toc = isRelease
    ? []
    : (post.body || []).filter((b) => b.kind === 'h2').map((b) => ({ id: b.id, text: b.text }));

  return (
    <PageShell
      currentPath="/blog"
      title={`${post.seoTitle || post.title} | ScopeWorth`}
      description={post.metaDescription || post.deck}
      canonical={BLOG_POST_CANONICAL(post.slug)}
      image={post.socialImage ? `https://scopeworth.app${post.socialImage}` : undefined}
    >
      <header className="ins-post-hero">
        <div className="ins-post-hero-bg" aria-hidden="true" />
        <div className="sw-container">
          <a className="ins-back" href={localizedHref('/blog')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M11 19l-7-7 7-7"/></svg>
            {t('blog.backAll')}
          </a>
          <div className="ins-post-hero-inner">
            <div className="ins-post-tags">
              <InsTag type={post.type} />
              {isRelease ? <InsVersion version={post.version} /> : null}
            </div>
            <h1 className="ins-post-title">{post.title}</h1>
            <p className="ins-post-deck">{post.deck}</p>
            <div className="ins-post-meta">
              <InsByline author={post.author} size="lg" />
              <span className="ins-post-meta-dates tabular">
                <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
                {post.readMins ? (
                  <>
                    <span className="ins-dot-sep" aria-hidden="true" />
                    {t('blog.minRead', { n: post.readMins })}
                  </>
                ) : null}
              </span>
            </div>
          </div>
        </div>
      </header>

      {post.heroImage ? (
        <div className="sw-container">
          <figure className="ins-post-visual">
            <img src={post.heroImage} alt={post.heroImageAlt || ''} width="1920" height="1080" />
            {post.heroImageCaption ? <figcaption>{post.heroImageCaption}</figcaption> : null}
          </figure>
        </div>
      ) : null}

      {post.disclosure ? (
        <div className="sw-container">
          <p className="ins-post-disclosure">{post.disclosure}</p>
        </div>
      ) : null}

      <div className="ins-post-body sw-container" style={toc.length ? undefined : { gridTemplateColumns: '1fr' }}>
        <div className="ins-post-main">
          {isRelease
            ? (post.changes || []).map((g, i) => <BlogChangeGroup key={i} group={g} />)
            : (post.body || []).map((b, i) => <BlogBlock key={i} block={b} />)}
        </div>
        {toc.length ? (
          <aside className="ins-post-rail">
            <BlogToc items={toc} />
          </aside>
        ) : null}
      </div>

      <BlogSampleCta />
      {relatedUseCases(post.slug, getActiveLocale()).length > 0 && <section className="sw-section"><div className="sw-container"><h2 className="sw-h2">{useCaseLabels(getActiveLocale()).related}</h2><ul>{relatedUseCases(post.slug, getActiveLocale()).map((item) => <li key={item.slug}><a className="sw-textlink" href={localizedHref(`/use-cases/${item.slug}`)}>{item.title}</a></li>)}</ul></div></section>}
      <BlogRelated slug={post.slug} />
    </PageShell>
  );
}
